/*
 * QuickPaste's settings, hands-on (sections/quickpaste-settings.liquid).
 *
 * The QuickPaste panel next to a small Premiere. Every paste follows the
 * plugin's own rules (copypaste-premiere/js/bridge-core.js runPasteImportChain
 * and jsx/host.jsx importAndPlaceJSX / placeTemplateJSX):
 *  - a file goes into the Paste To bin (Project Root, an existing bin, or a
 *    new one, made on the first paste or when Enter is pressed in Name);
 *  - with Paste at Playhead it also lands at the playhead on the lowest track
 *    that is empty for its whole length (a track is added if none is);
 *  - WebP becomes a PNG only with Convert Images to PNG on; HEIC always does;
 *  - plain text becomes a title at the playhead (titles are always placed);
 *    with Paste Text as Title off the text is skipped: nothing is pasted;
 *  - Copy Current Frame copies the frame as a PNG, and with Add Frame to
 *    Project Bin also imports it into the Paste To bin (never the timeline).
 * Both shortcut boxes record a new combination (modifier + letter or digit,
 * Esc cancels) and ↺ resets. The paste and copy-frame shortcuts work for real
 * while the pointer is over the demo or focus is in it (never elsewhere, and
 * the visitor's clipboard is never read).
 *
 * While on screen a short loop plays; any touch hands it over; Replay plays
 * it again. Off screen or in a hidden tab nothing runs.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent || '';
  const IS_MAC = /mac|iphone|ipad|ipod/i.test(platform);

  // the panel's own shortcut text (main.js formatShortcut): full names
  const fmt = (s) => {
    const parts = [];
    if (IS_MAC) {
      if (s.ctrl) parts.push('Ctrl');
      if (s.alt) parts.push('Option');
      if (s.shift) parts.push('Shift');
      if (s.meta) parts.push('Cmd');
    } else {
      if (s.meta || s.ctrl) parts.push('Ctrl');
      if (s.alt) parts.push('Alt');
      if (s.shift) parts.push('Shift');
    }
    parts.push(s.key);
    return parts.join(' + ');
  };
  const same = (a, b) => a.key === b.key && !!a.meta === !!b.meta && !!a.shift === !!b.shift && !!a.ctrl === !!b.ctrl && !!a.alt === !!b.alt;
  const DEFAULTS = {
    paste: { key: 'V', meta: true, shift: false, ctrl: false, alt: false },
    frame: { key: 'C', meta: true, shift: true, ctrl: false, alt: false },
  };
  const letterOf = (code) => (code.indexOf('Key') === 0 ? code.slice(3) : code.indexOf('Digit') === 0 ? code.slice(5) : null);
  // a keydown as a shortcut, the way main.js records it (Ctrl is "meta" on Windows)
  const comboOf = (e) => {
    const key = letterOf(e.code || '');
    if (!key) return null;
    if (!IS_MAC && e.metaKey) return null;
    return {
      key,
      meta: IS_MAC ? e.metaKey : e.ctrlKey,
      shift: e.shiftKey,
      ctrl: IS_MAC ? e.ctrlKey : false,
      alt: e.altKey,
    };
  };

  const ICONS = {
    bin: '<svg viewBox="0 0 16 16" fill="none" stroke="#b9a35a" stroke-width="1.2"><path d="M1.5 3.5a1 1 0 0 1 1-1h3.6l1.4 1.5h5.9a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1h-10.9a1 1 0 0 1-1-1z" fill="rgba(185,163,90,.18)"/></svg>',
    video: '<svg viewBox="0 0 16 16" fill="none" stroke="#8f9cff" stroke-width="1.2"><rect x="1.5" y="3" width="13" height="10" rx="1"/><path d="M4 3v10M12 3v10"/></svg>',
    image: '<svg viewBox="0 0 16 16" fill="none" stroke="#e284d2" stroke-width="1.2"><rect x="1.5" y="2.5" width="13" height="11" rx="1"/><path d="m2.5 12 3.5-3.5 2.5 2.5 2-2 3 3" stroke-linejoin="round"/></svg>',
    seq: '<svg viewBox="0 0 16 16" fill="none" stroke="#7fc8a9" stroke-width="1.2"><rect x="1.5" y="3" width="13" height="10" rx="1"/><path d="M1.5 6.5h13M1.5 9.5h13"/></svg>',
  };
  const MENU = {
    root: '<svg viewBox="0 0 16 16"><path d="M2 3.5h12M2 8h12M2 12.5h12"/></svg>',
    bin: '<svg viewBox="0 0 16 16"><path d="M1.5 3.5a1 1 0 0 1 1-1h3.6l1.4 1.5h5.9a1 1 0 0 1 1 1v7.5a1 1 0 0 1-1 1h-10.9a1 1 0 0 1-1-1z"/></svg>',
    add: '<svg viewBox="0 0 16 16"><path d="M8 3v10M3 8h10"/></svg>',
    check: '<svg viewBox="0 0 16 16"><path d="M3 8.5l3.2 3L13 4.5"/></svg>',
  };

  // timeline: 16 s wide, playhead at 5 s, stills and titles 5 s long
  const PH = 31.25;
  const LEN = 31.25;
  const MAX_V = 4;
  const ROOT = '';
  const NEW = ':new-bin:';

  // Visible text, English by default; the section's data-qs-i18n JSON (theme
  // locales) replaces it at init. [name] and [keys] are filled in here.
  const L = {
    project_root: 'Project Root',
    no_bin: 'No bin',
    bins: 'Bins',
    new_bin: 'New Bin…',
    new_bin_named: 'New bin: [name]',
    bin_named: '[name] bin',
    reset_to: 'Reset to [keys]',
    press_shortcut: 'Press a shortcut…',
    bin_footage: 'Footage',
    bin_graphics: 'Graphics',
    sequence_01: 'Sequence 01',
  };
  const fill = (text, map) => text.replace(/\[(\w+)\]/g, (m, k) => (k in map ? String(map[k]) : m));

  const init = (root) => {
    if (root.dataset.qsReady) return;
    root.dataset.qsReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => [...root.querySelectorAll(sel)];
    const stage = q('[data-qs-stage]');
    const cursor = q('[data-qs-cursor]');
    const treeEl = q('[data-qs-tree]');
    const tracksEl = q('[data-qs-tracks]');
    const layersEl = q('[data-qs-layers]');
    const shutter = q('[data-qs-shutter]');
    const statusEl = q('[data-qs-status]');
    const pasteBtn = q('[data-qs-paste]');
    const frameBtn = q('[data-qs-frame]');
    const dd = q('[data-qs-dd]');
    const ddBtn = q('[data-qs-dd-btn]');
    const ddLabel = q('[data-qs-dd-label]');
    const menu = q('[data-qs-menu]');
    const newRow = q('[data-qs-newrow]');
    const newInput = q('[data-qs-newname]');
    const replay = q('[data-qs-replay]');
    const pasteKey = q('[data-qs-pastekey]');
    const copyKey = q('[data-qs-copykey]');
    const switches = Object.fromEntries(qa('[data-qs-switch]').map((el) => [el.dataset.qsSwitch, el]));
    const fields = Object.fromEntries(qa('[data-qs-field]').map((el) => [el.dataset.qsField, el]));
    const resets = Object.fromEntries(qa('[data-qs-reset]').map((el) => [el.dataset.qsReset, el]));
    const chips = qa('[data-qs-copy]');
    if (!stage || !treeEl || !tracksEl) return;

    const i18n = q('[data-qs-i18n]');
    if (i18n) {
      try {
        const dict = JSON.parse(i18n.textContent);
        Object.keys(L).forEach((k) => {
          if (typeof dict[k] === 'string' && dict[k]) L[k] = dict[k];
        });
      } catch (error) {
        // keep the English
      }
    }

    const text = (q('[data-qs-text]') || {}).textContent || 'Summer Sale';
    const IMG = { webp: root.dataset.imgWebp, heic: root.dataset.imgHeic };
    const copy = (key, map) =>
      esc(root.dataset[key] || '').replace(/\[(name|bin|track|keys)\]/g, (m, k) => (map && k in map ? `<b>${esc(map[k])}</b>` : m));

    /* ---------- State ---------- */
    let st;
    let project;
    let clips;
    let vTracks;
    let fresh = {};
    let uid = 0;
    const blank = () => {
      st = {
        convert: true,
        title: true,
        playhead: true,
        frameBin: false,
        bin: ROOT,
        newName: '',
        paste: { ...DEFAULTS.paste },
        frame: { ...DEFAULTS.frame },
        copy: 'webp',
      };
      project = {
        bins: [
          { name: L.bin_footage, open: true, items: [{ name: 'interview.mp4', kind: 'video' }] },
          { name: L.bin_graphics, open: false, items: [{ name: 'logo.png', kind: 'image' }] },
        ],
        root: [{ name: L.sequence_01, kind: 'seq' }],
      };
      clips = [];
      vTracks = 3;
      fresh = {};
    };

    const binName = () => (st.bin === NEW ? st.newName.trim() : st.bin);
    const binLabel = (name) => (name ? fill(L.bin_named, { name }) : L.project_root);
    const findBin = (name) => project.bins.find((b) => b.name === name);
    // host.jsx resolveTargetBinJSX: find the top-level bin or make it
    const resolveBin = (name) => {
      if (!name) return null;
      let bin = findBin(name);
      if (!bin) {
        bin = { name, open: true, items: [] };
        project.bins.push(bin);
        fresh = { ...fresh, ['bin:' + name]: true };
      }
      return bin;
    };

    /* ---------- Render: the panel ---------- */
    const renderPanel = () => {
      Object.entries(switches).forEach(([k, el]) => el.setAttribute('aria-checked', String(!!st[k])));
      ['paste', 'frame'].forEach((k) => {
        if (fields[k] && !fields[k].classList.contains('is-recording')) fields[k].textContent = fmt(st[k]);
        if (resets[k]) {
          resets[k].disabled = same(st[k], DEFAULTS[k]);
          resets[k].title = fill(L.reset_to, { keys: fmt(DEFAULTS[k]) });
        }
      });
      if (pasteKey) pasteKey.textContent = fmt(st.paste);
      if (copyKey) copyKey.textContent = IS_MAC ? 'Cmd + C' : 'Ctrl + C';
      const typed = st.newName.trim();
      ddLabel.textContent = st.bin === ROOT ? L.project_root : st.bin === NEW ? (typed ? fill(L.new_bin_named, { name: typed }) : L.new_bin) : st.bin;
      newRow.hidden = st.bin !== NEW;
      if (newInput.value !== st.newName) newInput.value = st.newName;
      chips.forEach((c) => c.setAttribute('aria-checked', String(c.dataset.qsCopy === st.copy)));
    };

    /* ---------- Render: Project panel ---------- */
    const node = (name, kind, depth, extra = '') =>
      `<span class="qs-node${extra}" style="--d:${depth}"><span class="qs-tri${kind === 'bin' ? '' : ' qs-tri--none'}">▶</span>${ICONS[kind]}<span class="qs-nname">${esc(name)}</span></span>`;
    const renderTree = () => {
      const target = binName();
      let html = '';
      project.bins.forEach((b) => {
        const cls = (b.open ? ' is-open' : '') + (fresh['bin:' + b.name] ? ' is-new' : '') + (target === b.name ? ' is-target' : '');
        html += node(b.name, 'bin', 0, cls);
        if (b.open) b.items.forEach((it) => (html += node(it.name, it.kind, 1, fresh[it.id] ? ' is-new' : '')));
      });
      project.root.forEach((it) => (html += node(it.name, it.kind, 0, fresh[it.id] ? ' is-new' : '')));
      treeEl.innerHTML = html;
      // keep the newest item in view, inside the panel only
      const last = treeEl.querySelector('.qs-node.is-new:last-of-type') || [...treeEl.querySelectorAll('.qs-node.is-new')].pop();
      if (last) {
        const over = last.offsetTop + last.offsetHeight - treeEl.clientHeight + 6;
        treeEl.scrollTop = Math.max(0, over);
      } else treeEl.scrollTop = 0;
    };

    /* ---------- Render: timeline + monitor ---------- */
    const clipHTML = (c) =>
      `<span class="qs-clip qs-clip--${c.kind}${fresh[c.id] ? ' is-new' : ''}" style="--x:${c.x}%;--w:${c.w}%"><span>${esc(c.name)}</span></span>`;
    const renderTimeline = (ghost) => {
      let html = '';
      for (let t = vTracks; t >= 1; t -= 1) {
        let lane = '';
        if (t === 1) lane += clipHTML({ kind: 'video', x: 0, w: 58, name: 'interview.mp4' }) + clipHTML({ kind: 'video', x: 58.4, w: 41.6, name: 'interview.mp4' });
        clips.filter((c) => c.track === t).forEach((c) => (lane += clipHTML(c)));
        if (ghost && ghost === t) lane += `<span class="qs-ghost" style="--x:${PH}%;--w:${LEN}%"></span>`;
        html += `<span class="qs-track${fresh['track:' + t] ? ' is-added' : ''}"><span class="qs-tlabel">V${t}</span><span class="qs-lane">${lane}</span></span>`;
      }
      html += `<span class="qs-track qs-track--a"><span class="qs-tlabel">A1</span><span class="qs-lane">${clipHTML({ kind: 'audio', x: 0, w: 58, name: '' })}${clipHTML({ kind: 'audio', x: 58.4, w: 41.6, name: '' })}</span></span>`;
      tracksEl.innerHTML = html;

      // the monitor shows what is at the playhead, top track over bottom
      let layers = '';
      clips
        .slice()
        .sort((a, b) => a.track - b.track)
        .forEach((c) => {
          const n = fresh[c.id] ? ' is-new' : '';
          if (c.kind === 'image') layers += `<img class="qs-layer${n}" src="${esc(c.img)}" alt="" width="640" height="360" decoding="async">`;
          else layers += `<span class="qs-titlelayer qs-layer${n}">${esc(c.name)}</span>`;
        });
      layersEl.innerHTML = layers;
    };

    const render = () => {
      renderPanel();
      renderTree();
      renderTimeline();
    };

    const say = (html, kind) => {
      statusEl.innerHTML = `<span>${html}</span>`;
      statusEl.className = `qs-status${kind ? ' is-' + kind : ''}`;
      void statusEl.offsetWidth;
      statusEl.classList.add('is-fresh');
    };

    /* ---------- The plugin's rules ---------- */
    // host.jsx findOrMakeRoomJSX: the lowest track free for the clip's whole
    // length; a track is added if none is (the demo starts over past V4)
    const freeTrack = () => {
      for (let t = 2; t <= vTracks; t += 1) if (!clips.some((c) => c.track === t)) return t;
      if (vTracks < MAX_V) {
        vTracks += 1;
        fresh['track:' + vTracks] = true;
        return vTracks;
      }
      clips = [];
      vTracks = 3;
      return 2;
    };

    const paste = () => {
      fresh = {};
      const flash = (el) => {
        el.classList.remove('is-press');
        void el.offsetWidth;
        el.classList.add('is-press');
        window.setTimeout(() => el.classList.remove('is-press'), 160);
      };
      flash(pasteBtn);

      if (st.copy === 'text') {
        if (!st.title) {
          render();
          say(copy('statusSkip'), 'skip');
          return;
        }
        const track = freeTrack();
        const id = `c${(uid += 1)}`;
        clips.push({ id, track, kind: 'title', x: PH, w: LEN, name: text });
        fresh[id] = true;
        render();
        say(copy('statusTitle', { name: text, track: `V${track}` }), 'ok');
        return;
      }

      const name = st.copy === 'heic' ? 'IMG_0412.png' : st.convert ? 'photo.png' : 'photo.webp';
      const target = binName();
      const bin = resolveBin(target);
      const id = `c${(uid += 1)}`;
      const item = { id, name, kind: 'image' };
      if (bin) {
        bin.open = true;
        bin.items.push(item);
      } else project.root.push(item);
      fresh[id] = true;
      if (st.bin === NEW && target) {
        // the new bin now exists: the dropdown lists it and selects it
        st.bin = target;
        st.newName = '';
      }

      if (st.playhead) {
        const track = freeTrack();
        const cid = `t${id}`;
        clips.push({ id: cid, track, kind: 'image', x: PH, w: LEN, name, img: IMG[st.copy] });
        fresh[cid] = true;
        render();
        say(copy('statusPlaced', { name, bin: binLabel(target), track: `V${track}` }), 'ok');
      } else {
        render();
        let spotT = 0;
        for (let t = vTracks; t >= 2; t -= 1) if (!clips.some((c) => c.track === t)) spotT = t;
        renderTimeline(spotT);
        say(copy('statusBin', { name, bin: binLabel(target) }), 'ok');
      }
    };

    const copyFrame = () => {
      fresh = {};
      frameBtn.classList.remove('is-press');
      void frameBtn.offsetWidth;
      frameBtn.classList.add('is-press');
      window.setTimeout(() => frameBtn.classList.remove('is-press'), 160);
      shutter.classList.remove('is-on');
      void shutter.offsetWidth;
      shutter.classList.add('is-on');
      if (!st.frameBin) {
        render();
        say(copy('statusFrame'), 'ok');
        return;
      }
      const target = binName();
      const bin = resolveBin(target);
      const id = `c${(uid += 1)}`;
      const item = { id, name: `${L.sequence_01} Frame.png`, kind: 'image' };
      if (bin) {
        bin.open = true;
        bin.items.push(item);
      } else project.root.push(item);
      fresh[id] = true;
      if (st.bin === NEW && target) {
        st.bin = target;
        st.newName = '';
      }
      render();
      say(copy('statusFrameBin', { name: item.name, bin: binLabel(target) }), 'ok');
    };

    /* ---------- Paste To dropdown ---------- */
    const item = (value, label, icon, hint) => {
      const sel = st.bin === value;
      return `<button type="button" class="qpp-item" role="option" aria-selected="${sel}" data-qs-bin="${esc(value)}"><span class="qpp-check">${sel ? MENU.check : ''}</span><span class="qpp-item-icon">${MENU[icon]}</span><span class="qpp-item-label">${esc(label)}</span>${hint ? `<span class="qpp-item-hint">${esc(hint)}</span>` : ''}</button>`;
    };
    const openMenu = () => {
      let html = item(ROOT, L.project_root, 'root', L.no_bin);
      html += `<span class="qpp-menu-div"></span><span class="qpp-menu-head">${esc(L.bins)}</span>`;
      project.bins.forEach((b) => (html += item(b.name, b.name, 'bin')));
      html += '<span class="qpp-menu-div"></span>' + item(NEW, L.new_bin, 'add');
      menu.innerHTML = html;
      dd.classList.add('is-open');
      ddBtn.setAttribute('aria-expanded', 'true');
    };
    const closeMenu = () => {
      dd.classList.remove('is-open');
      ddBtn.setAttribute('aria-expanded', 'false');
    };
    const binItem = (value) => [...menu.querySelectorAll('[data-qs-bin]')].find((el) => el.dataset.qsBin === value);
    const chooseBin = (value, focusName) => {
      closeMenu();
      st.bin = value;
      if (value === NEW) st.newName = '';
      fresh = {};
      render();
      if (value === NEW && focusName) newInput.focus();
    };

    /* ---------- Shortcut recording ---------- */
    let recording = null;
    let lastStatus = '';
    const stopRecording = () => {
      if (!recording) return;
      fields[recording].classList.remove('is-recording');
      recording = null;
      renderPanel();
    };
    const startRecording = (k) => {
      stopRecording();
      recording = k;
      lastStatus = statusEl.innerHTML;
      fields[k].classList.add('is-recording');
      fields[k].textContent = L.press_shortcut;
      say(copy('statusRecording'));
    };
    const commit = (k, combo) => {
      st[k] = combo;
      stopRecording();
      fields[k].classList.remove('is-changed');
      void fields[k].offsetWidth;
      fields[k].classList.add('is-changed');
      say(copy('statusShortcut', { keys: fmt(combo) }), 'ok');
    };

    /* ---------- The visitor's input ---------- */
    let mode = 'auto';
    let run = 0;
    let playing = false;
    let inView = false;
    let over = false;

    root.addEventListener('click', (event) => {
      const t = event.target.closest('button');
      if (!t || !root.contains(t)) return;
      if (t.hasAttribute('data-qs-replay')) return;
      if (t.hasAttribute('data-qs-paste')) paste();
      else if (t.hasAttribute('data-qs-frame')) copyFrame();
      else if (t.dataset.qsSwitch) {
        st[t.dataset.qsSwitch] = !st[t.dataset.qsSwitch];
        renderPanel();
      } else if (t.dataset.qsField) {
        if (recording === t.dataset.qsField) {
          stopRecording();
          statusEl.innerHTML = lastStatus;
        } else startRecording(t.dataset.qsField);
      } else if (t.dataset.qsReset) commit(t.dataset.qsReset, { ...DEFAULTS[t.dataset.qsReset] });
      else if (t.hasAttribute('data-qs-dd-btn')) {
        if (dd.classList.contains('is-open')) closeMenu();
        else {
          openMenu();
          const sel = menu.querySelector('[aria-selected="true"]') || menu.querySelector('[data-qs-bin]');
          if (sel && event.detail === 0) sel.focus();
        }
      } else if ('qsBin' in t.dataset) chooseBin(t.dataset.qsBin, true);
      else if (t.dataset.qsCopy) {
        st.copy = t.dataset.qsCopy;
        renderPanel();
      }
    });

    // a tap anywhere else closes the menu and stops a recording
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (dd.classList.contains('is-open') && !dd.contains(event.target)) closeMenu();
        if (recording && !fields[recording].contains(event.target)) {
          stopRecording();
          statusEl.innerHTML = lastStatus;
        }
      },
      true
    );

    menu.addEventListener('keydown', (event) => {
      const items = [...menu.querySelectorAll('[data-qs-bin]')];
      const i = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = items[(i + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
        if (next) next.focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        ddBtn.focus();
      } else if (event.key === 'Tab') closeMenu();
    });

    newInput.addEventListener('input', () => {
      st.newName = newInput.value;
      renderPanel();
    });
    newInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const name = st.newName.trim();
      if (!name) return;
      // main.js: Enter makes (or finds) the bin right away and selects it
      fresh = {};
      resolveBin(name);
      st.bin = name;
      st.newName = '';
      render();
      say(copy('statusBinMade', { bin: name }), 'ok');
      ddBtn.focus();
    });
    newInput.addEventListener('blur', () => {
      // an empty name falls back to Project Root
      if (st.bin === NEW && !st.newName.trim()) {
        st.bin = ROOT;
        renderPanel();
        renderTree();
      }
    });

    stage.addEventListener('pointerenter', () => (over = true));
    stage.addEventListener('pointerleave', () => (over = false));

    document.addEventListener(
      'keydown',
      (event) => {
        if (!root.isConnected) return;
        if (recording) {
          event.preventDefault();
          event.stopPropagation();
          if (event.repeat) return;
          if (event.key === 'Escape') {
            stopRecording();
            statusEl.innerHTML = lastStatus;
            return;
          }
          const combo = comboOf(event);
          if (!combo || !(combo.meta || combo.ctrl || combo.alt || combo.shift)) return;
          commit(recording, combo);
          return;
        }
        if (event.repeat || !(over || stage.contains(document.activeElement))) return;
        const tag = event.target && event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (event.target && event.target.isContentEditable)) return;
        const combo = comboOf(event);
        if (!combo) return;
        if (same(combo, st.paste)) {
          event.preventDefault();
          handOver();
          paste();
        } else if (same(combo, st.frame)) {
          event.preventDefault();
          handOver();
          copyFrame();
        }
      },
      true
    );

    /* ---------- The loop ---------- */
    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run && root.isConnected ? resolve() : reject(STOP)), ms);
      });
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width * fx, b.top - s.top + b.height * fy];
    };
    const setCursor = ([x, y]) => {
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
    };
    // a short panel scrolls inside (phones): bring the control into view first
    const content = q('.qpp-content');
    const reveal = async (r, el) => {
      if (!content || !content.contains(el) || content.scrollHeight <= content.clientHeight + 1) return;
      const c = content.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      if (b.top >= c.top + 8 && b.bottom <= c.bottom - 8) return;
      const top = content.scrollTop + (b.top - c.top) - (c.height - b.height) / 2;
      content.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      await wait(r, 360);
    };
    const moveTo = async (r, el, fx, fy, ms = 520) => {
      await reveal(r, el);
      setCursor(spot(el, fx, fy));
      return wait(r, ms);
    };
    const jumpTo = (el, fx, fy) => {
      cursor.style.transition = 'none';
      setCursor(spot(el, fx, fy));
      void cursor.offsetWidth;
      cursor.style.transition = '';
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 170);
    };
    const clearPointer = () => {
      cursor.classList.remove('is-on', 'is-click');
      qa('.is-hover').forEach((el) => el.classList.remove('is-hover'));
    };

    const reset = () => {
      stage.classList.add('is-instant');
      stopRecording();
      closeMenu();
      blank();
      render();
      if (content) content.scrollTop = 0;
      say(copy('statusReady') || esc(statusEl.dataset.ready || ''));
      clearPointer();
      void stage.offsetWidth;
      stage.classList.remove('is-instant');
    };
    statusEl.dataset.ready = statusEl.textContent;
    root.dataset.statusReady = statusEl.textContent;

    const click = async (r, el, fx, fy, ms) => {
      await moveTo(r, el, fx, fy, ms);
      el.classList.add('is-hover');
      await tap(r);
      el.classList.remove('is-hover');
    };

    const loop = async (r) => {
      // 1. a paste with the defaults: a PNG in Project Root, on V2
      if (content) content.scrollTop = 0;
      jumpTo(pasteBtn, 0.62, 0.62);
      cursor.classList.add('is-on');
      await wait(r, 220);
      await tap(r);
      paste();
      await wait(r, 1150);

      // 2. Paste To: Graphics
      await click(r, ddBtn, 0.55, 0.55, 560);
      openMenu();
      await wait(r, 300);
      const g = binItem(L.bin_graphics);
      await moveTo(r, g, 0.4, 0.55, 420);
      g.classList.add('is-hover');
      await wait(r, 160);
      await tap(r);
      chooseBin(L.bin_graphics);
      await wait(r, 260);

      // 3. Convert Images to PNG off, paste: photo.webp in Graphics, on V3
      await click(r, switches.convert, 0.5, 0.55, 520);
      st.convert = false;
      renderPanel();
      await wait(r, 280);
      await click(r, pasteBtn, 0.62, 0.6, 600);
      paste();
      await wait(r, 1300);

      // 4. Paste at Playhead off, paste: bin only, the timeline untouched
      await click(r, switches.playhead, 0.5, 0.55, 640);
      st.playhead = false;
      renderPanel();
      await wait(r, 280);
      await click(r, pasteBtn, 0.62, 0.6, 640);
      paste();
      await wait(r, 1500);
      cursor.classList.remove('is-on');
      await wait(r, 700);

      stage.classList.add('is-fade');
      await wait(r, 400);
      reset();
      stage.classList.remove('is-fade');
      await wait(r, 200);
    };

    const play = async () => {
      run += 1;
      const r = run;
      playing = true;
      reset();
      try {
        for (;;) await loop(r);
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      stage.classList.remove('is-fade');
      reset();
    };

    // the finished picture: the loop's three pastes, at rest
    const finished = () => {
      blank();
      paste();
      st.bin = L.bin_graphics;
      st.convert = false;
      paste();
      st.playhead = false;
      paste();
      fresh = {};
      render();
    };

    const handOver = () => {
      if (mode === 'hands') return;
      mode = 'hands';
      run += 1;
      playing = false;
      stage.classList.remove('is-fade', 'is-instant');
      clearPointer();
      root.classList.add('is-hands');
      statusEl.setAttribute('aria-live', 'polite');
      if (replay) replay.hidden = false;
    };
    stage.addEventListener('pointerdown', (event) => event.isTrusted && handOver(), true);
    stage.addEventListener('focusin', handOver);

    if (replay)
      replay.addEventListener('click', () => {
        mode = 'auto';
        root.classList.remove('is-hands');
        statusEl.removeAttribute('aria-live');
        replay.hidden = true;
        if (still.matches) {
          finished();
          handOver();
          return;
        }
        play();
      });

    blank();
    render();

    if (still.matches || !('IntersectionObserver' in window)) {
      finished();
      handOver();
      return;
    }

    let io = null;
    const update = () => {
      if (!root.isConnected) {
        run += 1;
        if (io) io.disconnect();
        document.removeEventListener('visibilitychange', update);
        return;
      }
      if (mode === 'hands') return;
      const go = inView && !document.hidden;
      if (go && !playing) play();
      else if (!go && playing) stop();
    };

    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.2 }
    );
    io.observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-qp-settings]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
